'use client';

import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { appRoutes } from '@/appRoutes';
import { useBootstrapAuth } from '@/features/auth/hooks/useBootstrapAuth';
import { useCreateWorkspaceMutation } from '@/features/workspaces/api/workspacesApi';
import { newWorkspaceSchema } from '@/features/workspaces/schemas';
import { setCurrentWorkspace } from '@/features/workspaces/slices/workspacesSlice';
import { useAppDispatch } from '@/store/hooks';
import { MainButton, Modal, SecondaryButton, SmartInput } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

type Props = {
  onCreated?: () => void;
};

export function CreateWorkspaceButton({ onCreated }: Props) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const bootstrapAuth = useBootstrapAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [createWorkspace, { isLoading }] = useCreateWorkspaceMutation();

  const handleCreate = async () => {
    if (!name.trim()) return;

    try {
      const res = await createWorkspace({
        name: name.trim(),
        logo_asset_key: '',
      }).unwrap();

      if (res.success) {
        notify({
          type: 'success',
          message: res.message || 'Workspace created',
        });

        dispatch(setCurrentWorkspace(res?.data?.workspace ?? null));
        bootstrapAuth({
          currentUser: res.data?.user ?? null,
          currentWorkspaceSlug: res.data?.workspace?.slug ?? null,
          isWorkspaceSetupRequired:
            res.data?.is_workspace_setup_required ?? null,
          isWorkspaceOnboarded:
            res.data?.workspace?.is_workspace_onboarded ?? null,
        });

        // * Call the onCreated callback if it is provided.
        // = might be useful for updating the state of the parent component.
        onCreated?.();

        // * Route to '/' so proxy fetches fresh user and redirects to /onboarding.
        router.replace(appRoutes.home);
      } else {
        throw new Error(res.message || 'Failed to create workspace');
      }
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <MainButton
          ariaLabel="Create new workspace"
          onClick={() => setIsOpen(true)}
        >
          Create new workspace
        </MainButton>
      </div>

      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        width="min(520px, 92vw)"
      >
        <div className="card-container-glass">
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.4rem' }}>
            Create a new Workspace
          </h3>
          <p style={{ margin: '0 0 1rem' }}>
            Enter a name for your new workspace. You can set slug and domain in
            onboarding.
          </p>
          <SmartInput
            type="text"
            name="workspace_name"
            label="Workspace name"
            value={name}
            onChange={setName}
            schema={newWorkspaceSchema.shape.name}
          />
          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end',
              marginTop: '1rem',
            }}
          >
            <SecondaryButton
              ariaLabel="Cancel"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </SecondaryButton>
            <MainButton
              onClick={handleCreate}
              isLoading={isLoading}
              disabled={!name.trim()}
            >
              Create
            </MainButton>
          </div>
        </div>
      </Modal>
    </>
  );
}
